import { Eye, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useExitImpersonation } from '../master/hooks/useMasterData';

interface ImpersonationBannerProps {
  workspaceName: string;
  /** Chamado após `/impersonate/exit` ter terminado com sucesso. */
  onExit: () => void;
}

/**
 * Faixa lilás compacta exibida abaixo da status bar quando um usuário
 * Master está impersonando um workspace. Botão "Sair" encerra a sessão
 * de impersonação no backend e devolve o user ao Master mobile.
 */
export function ImpersonationBanner({ workspaceName, onExit }: ImpersonationBannerProps) {
  const exit = useExitImpersonation();

  function handleExit() {
    exit.mutate(undefined, {
      onSuccess: () => {
        toast.success('Voltando ao modo Master');
        onExit();
      },
      onError: (err) => {
        toast.error((err as Error).message || 'Falha ao sair da impersonação');
      },
    });
  }

  return (
    <div
      role="status"
      aria-label={`Impersonando ${workspaceName}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 14px',
        background: 'var(--lilac-deep)',
        color: '#fff',
        fontSize: 11.5,
        flexShrink: 0,
      }}
    >
      <Eye size={13} strokeWidth={2} />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        Impersonando <strong style={{ fontWeight: 700 }}>{workspaceName}</strong>
      </span>
      <button
        onClick={handleExit}
        disabled={exit.isPending}
        aria-label="Sair da impersonação"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          padding: '4px 10px',
          borderRadius: 999,
          background: 'rgba(255,255,255,0.18)',
          color: '#fff',
          border: '1px solid rgba(255,255,255,0.25)',
          fontSize: 11,
          fontWeight: 600,
          cursor: exit.isPending ? 'wait' : 'pointer',
          opacity: exit.isPending ? 0.7 : 1,
          font: 'inherit',
        }}
      >
        {exit.isPending ? <Loader2 size={11} className="animate-spin" /> : <X size={11} strokeWidth={2.2} />}
        Sair
      </button>
    </div>
  );
}
